import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, HELP_IMG } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, Truck, FlaskConical, BookOpen, Microscope, TestTube2, ChevronLeft, ChevronRight } from "lucide-react";

const TRUST = [
  { icon: ShieldCheck, title: "Lot-traceable QC", body: "Every product comes with a Certificate of Analysis and full lot traceability." },
  { icon: Truck, title: "Pan-India logistics", body: "Cold-chain shipping with same-week delivery across major research hubs." },
  { icon: FlaskConical, title: "Application support", body: "Free protocol design and troubleshooting from in-house scientists." },
  { icon: BookOpen, title: "Open documentation", body: "Datasheets, SDS, and application notes available for every catalog item." },
];

const CAT_VISUAL = {
  enzymes: { color: "from-primary to-primary/70", icon: TestTube2 },
  reagents: { color: "from-secondary to-secondary/70", icon: FlaskConical },
  kits: { color: "from-accent to-accent/70", icon: BookOpen },
  consumables: { color: "from-primary/80 to-secondary/60", icon: Microscope },
  instruments: { color: "from-secondary/80 to-primary/60", icon: Microscope },
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newArr, setNewArr] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?w=900",
      title: "Featured: NiviHi-Fi Polymerase",
      subtitle: "100× higher fidelity than Taq · NL-DP-002",
    },
    {
      image: "https://images.unsplash.com/photo-1532187643603-ba119ca4109e?w=900",
      title: "Advanced Molecular Biology",
      subtitle: "Research-grade reagents and enzymes",
    },
    {
      image: "https://images.unsplash.com/photo-1576086213369-97a306d36557?w=900",
      title: "Trusted Laboratory Solutions",
      subtitle: "Built for reproducible results",
    },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  const getCategoryImageUrl = (category) => {
    const imagePath = category.image_url || category.imgage_url || "";
    if (!imagePath) return "";
    return imagePath.startsWith("http") ? imagePath : `${BACKEND_URL}${imagePath}`;
  };

  const getSlideImageUrl = (item, fallbackImage) => {
    const imagePath = item.image_url || item.imgage_url || fallbackImage || "";
    if (!imagePath) return "";
    return imagePath.startsWith("http") ? imagePath : `${BACKEND_URL}${imagePath}`;
  };

  const slides = categories.length > 0
    ? categories.map((category) => {
        const subs = category.subcategories || [];
        const visible = subs.slice(0, 3).map((s) => s.name).filter(Boolean);
        const extra = Math.max(0, subs.length - visible.length);
        const subtitle = visible.length > 0 ? (extra > 0 ? `${visible.join(" · ")} · +${extra} more` : visible.join(" · ")) : category.name;
        return {
          image: getCategoryImageUrl(category),
          title: category.name,
          subtitle,
        };
      })
    : heroSlides;

  const activeSlides = slides.length > 0 ? slides : heroSlides;

  const categoryGridClass = categories.length === 0
    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-5"
    : categories.length === 1
      ? "grid-cols-1 justify-items-center"
      : categories.length === 2
        ? "grid-cols-1 sm:grid-cols-2 justify-items-center"
        : categories.length === 3
          ? "grid-cols-1 sm:grid-cols-3 justify-items-center"
          : categories.length === 4
            ? "grid-cols-2 md:grid-cols-4 justify-items-center"
            : "grid-cols-2 md:grid-cols-3 lg:grid-cols-5";

  const categoryCardClass = `group relative rounded-2xl overflow-hidden bg-white border border-gray-100 hover:border-primary/30 transition-all aspect-[4/5] ${categories.length < 5 ? "max-w-[320px]" : ""}`;

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => { });
    api.get("/products/featured").then((r) => setFeatured(r.data)).catch(() => { });
    api.get("/products/new").then((r) => {
  setNewArr(r.data);
}).catch((err) => {
  console.error("NEW PRODUCTS ERROR:", err);
});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % activeSlides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [activeSlides.length]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="hero-gradient bg-bio-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 rise">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" /> New launch · nivilabs
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight mt-5 text-balance">
              Premium products,<br className="hidden sm:block" />
              <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                engineered for reproducibility.
              </span>
            </h1>
            <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
              At <b>niviLabs</b>, we manufacture and deliver premium reagents engineered for absolute reproducibility. Born from Bionivid’s decade of genomics expertise, we are a trusted manufacturing partner dedicated to advancing life-science research. Streamline your procurement and directly source our high-quality enzymes, kits, reagents, consumables, and laboratory instruments all backed by a seamless, <b>one-click Request-for-Quote (RFQ) system</b>.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 h-12 px-6 rounded-full" data-testid="hero-browse-btn">
                <Link to="/products">Browse catalog <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-primary/10 shadow-[0_30px_80px_-30px_rgba(4,120,87,0.35)]">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-out"
                  style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                >
                  {activeSlides.map((slide, index) => (
                    <div key={`${slide.title}-${index}`} className="min-w-full">
                      <img
                        src={slide.image || heroSlides[0].image}
                        alt={slide.title || "Featured slide"}
                        className="w-full h-[480px] object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 via-transparent to-secondary/10" />

              {/* Navigation */}
              <button
                onClick={() =>
                  setCurrentSlide(
                    currentSlide === 0
                      ? activeSlides.length - 1
                      : currentSlide - 1
                  )
                }
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-lg"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <button
                onClick={() =>
                  setCurrentSlide(
                    (currentSlide + 1) % activeSlides.length
                  )
                }
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-lg"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              {/* Featured Card */}
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-xl p-4 border border-white/50 shadow-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <FlaskConical className="w-5 h-5" />
                  </div>

                  <div>
                    <div className="text-sm font-semibold">
                      {activeSlides[currentSlide]?.title || heroSlides[0].title}
                    </div>

                    <div className="text-xs text-muted-foreground">
                      {activeSlides[currentSlide]?.subtitle || heroSlides[0].subtitle}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="categories-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold">Shop by</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Product categories</h2>
          </div>
          <Link to="/products" className="hidden sm:inline-flex items-center text-sm text-primary font-medium hover:underline">
            View all <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className={`grid gap-4 lg:gap-6 ${categoryGridClass}`}> 
          {categories.map((c, i) => {
            const v = CAT_VISUAL[c.slug] || CAT_VISUAL.enzymes;
            const Icon = v.icon;
            const subcategories = c.subcategories || [];
            const visibleSubcategories = subcategories.slice(0, 3);
            const extraCount = subcategories.length - visibleSubcategories.length;
            return (
              <Link key={c.slug} to={`/products?category=${c.slug}`}
                data-testid={`cat-card-${c.slug}`}
                className={categoryCardClass}>
                <img src={c.image_url} alt={c.name} className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-500" />
                <div className={`absolute inset-0 bg-gradient-to-tr ${v.color} opacity-70 group-hover:opacity-80`} />
                <div className="relative h-full flex flex-col justify-end p-5 text-white">
                  <Icon className="w-7 h-7 mb-2 opacity-90" />
                  <h3 className="font-display text-xl font-bold leading-tight">{c.name}</h3>
                  {visibleSubcategories.length > 0 && (
                    <p className="text-xs opacity-90 mt-1 leading-snug">
                      {visibleSubcategories.map((s, idx) => (
                        <span key={s.slug || s.name}>
                          {idx > 0 && " · "}
                          {s.name}
                        </span>
                      ))}
                      {extraCount > 0 && ` · +${extraCount} more`}
                    </p>
                  )}
                  <p className="text-xs opacity-90 mt-2 line-clamp-2">{c.description}</p>
                  <div className="mt-3 inline-flex items-center text-xs font-semibold">
                    Explore <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="featured-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-primary font-semibold">Most requested</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">Featured products</h2>
          </div>
          <Link to="/products" className="hidden sm:inline-flex items-center text-sm text-primary font-medium hover:underline">
            All products <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* TRUST */}
      <section className="bg-white border-y border-gray-100 mt-12" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Why choose nivilabs?</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              <strong>Built by Biologists, for Biologists.</strong> Born from a decade of pioneering genomic research, our reagents are developed and validated by scientists, for scientists. We don't just supply the bench; we advance it.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <div className="text-base font-semibold">🧬 10+ Years of Wet-Lab Expertise</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Products engineered by genomics pioneers who understand the frustration of extracting nucleic acid from difficult samples. Our solutions are born from real-world experience handling complex, challenging tissues.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <div className="text-base font-semibold">🧪 Uncompromising Reproducibility</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Every enzyme, kit, and reagent is strictly tested to deliver the consistent results your research demands. We guarantee high-yield, high-purity isolation to eliminate experimental downtime.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6 shadow-sm">
              <div className="text-base font-semibold">🚀 Driven by Innovation</div>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                A comprehensive portfolio designed to eliminate experimental bottlenecks, streamline your workflows, and accelerate breakthrough scientific discoveries worldwide.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* NEW ARRIVALS */}
      {newArr.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="new-arrivals-section">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs tracking-[0.2em] uppercase text-secondary font-semibold">Just landed</div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold mt-2">New arrivals</h2>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {newArr.slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <section className="bg-slate-50 border-b border-gray-200 py-16" data-testid="specialist-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="overflow-hidden rounded-[2rem] bg-white border border-slate-200 shadow-sm">
            <img
              src={HELP_IMG}
              alt="Lab equipment and scientist tools on a clean bench"
              className="w-full h-72 object-cover sm:h-[420px]"
            />
            <div className="mt-5">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-display text-3xl sm:text-4xl font-bold">Speak with our Scientist Team</h2>
                <p className="mt-4 text-muted-foreground leading-relaxed text-base sm:text-lg">
                  <strong>Struggling with a tough protocol or looking to optimize your workflow?</strong>
                  Our team of molecular biology specialists is ready to support your research.
                  From selecting the right tools to troubleshooting complex extractions, our scientists provide actionable,
                  expert guidance at every stage of your project.
                </p>
              </div>
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                  <div className="font-semibold text-base">Advanced DNA/RNA Extraction</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Solutions engineered for high-quality, high-purity results even from the most challenging tissues.
                  </p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                  <div className="font-semibold text-base">Molecular Biology Essentials</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Premium DNA ladders and precision-formulated buffers to support your core workflows.
                  </p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                  <div className="font-semibold text-base">High-Performance Enzymes</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    Robust, reliable enzymes designed for consistent batch-to-batch performance.
                  </p>
                </div>
                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-6">
                  <div className="font-semibold text-base">Precision Real-Time PCR</div>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    High-sensitivity qPCR kits engineered for accurate, reproducible quantification.
                  </p>
                </div>
              </div>
              <div className="mt-10 border-t border-slate-200 pt-8 text-center">
                <p className="text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                  Partner with nivilabs for <strong>in-house developed solutions</strong>, seamless workflows, and reliable scientific success.
                </p>
                <Link to="/contact" data-testid="specialist-contact-btn" className="mt-6 inline-flex bg-primary text-white hover:bg-primary/90 rounded-full h-12 px-8 items-center justify-center font-semibold">
                  Get in touch with a specialist today to accelerate your research
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-b border-gray-200 py-16" data-testid="vision-mission-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="text-sm tracking-[0.2em] uppercase text-primary font-semibold">Our Vision</div>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                By developing products engineered from a decade of genomic expertise, <strong>nivilabs</strong> is committed to being your trusted partner in scientific excellence.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                Whether you are working in academic research, clinical diagnostics, or industrial biotechnology, we build the tools you can rely on to advance scientific knowledge and enable breakthrough discoveries.
              </p>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="text-sm tracking-[0.2em] uppercase text-primary font-semibold">Our Mission</div>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                To accelerate global scientific discovery by developing high-quality molecular biology products at affordable prices.
              </p>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                We believe budget constraints should never compromise research integrity. By engineering premium-grade, reproducible reagents and extraction kits in-house and delivering them at accessible price points, nivilabs enables labs worldwide to achieve results without paying a premium.
              </p>
            </div>
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="cta-section">
        <div className="rounded-3xl bg-gradient-to-r from-primary to-primary/80 p-10 lg:p-14 text-white relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="relative grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <h3 className="font-display text-3xl sm:text-4xl font-bold leading-tight">
                Can't find a product? Submit a custom inquiry.
              </h3>
              <p className="mt-3 text-white/80 max-w-2xl">
                Our scientific procurement team can source specialty reagents, OEM consumables,
                and bulk volumes from validated global suppliers.
              </p>
            </div>
            <div className="flex md:justify-end gap-3">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full h-12 px-6" data-testid="cta-contact-btn">
                <Link to="/contact">Contact us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}